/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// $('ul.main-nav.mob-old-menu details.js-mega-nav summary.main-nav__item--toggle.relative.js-nav-hover.js-toggle').mouseenter(function(){
// setTimeout(function(){

// var geht=$('.main-nav__child.mega-nav.mega-nav--sidebar.has-motion').outerHeight();
//       console.log(geht+'----rrr')
//     $('.top_men_cstm.container,ul.sprate_lin.child-nav').css('height',geht+'px')
//  var gehts=geht+30;
//   $('.banner_are').css('height',gehts+'px')
// },400)

// })
function checkimage() {
  var vls = $(
    'form.js-instalments-form.text-sm.mt-2 input[type="hidden"][name="id"]'
  ).val();

  var mid = $('.product_drop option[vid="' + vls + '"]').attr("mid");
  var gtindex = $('.slider-banner-image[mediaid="' + mid + '"]')
    .parent()
    .parent()
    .index();

  // Can't find mid default first
  gtindex = gtindex < 0 ? 0 : gtindex;

  $(".slider-for").slick("slickGoTo", gtindex);
  $(".slider.slider-nav.thumb-image.no_slides .thumbnail-image").removeClass(
    "active"
  );
  $(".slider.slider-nav.thumb-image.no_slides .thumbnail-image")
    .eq(gtindex)
    .addClass("active");
}
checkimage();
$("body").on("click", ".opt-label--image,.opt-label--btn", function () {
  setTimeout(function () {
    checkimage();
  }, 100);
});
$(
  'summary.main-nav__item--toggle.relative.js-nav-hover.js-toggle[menu="SHOP"]'
).mouseenter(function () {
  setTimeout(function () {
    var geht = $(
      ".main-nav__child.mega-nav.mega-nav--sidebar.has-motion"
    ).outerHeight();

    var gehoutrt = $("ul.sprate_lin.child-nav").outerHeight();
    var str = gehoutrt - 23;

    $(
      'ul.sprate_lin.child-nav,.mega_menu_right,.disclosure__panel.has-motion[link="brands"]'
    ).css("height", gehoutrt + "px");
    $(".top_men_cstm.container").css("height", "712px");
    $(".main-nav__child.mega-nav.mega-nav--sidebar.has-motion").css({
      height: gehoutrt + "px",
      height: gehoutrt + "px",
    });
    $(".banner_are").css("height", gehoutrt + "px");
  }, 300);
});
/* ---- Menu bindings that must also attach to lazy-injected nav content ----
   The Shop mega panel and the mobile menu are no longer in the initial HTML;
   mega-nav-lazy-load.js injects them and fires a bubbling "nav-panel:injected"
   event from the parent element after each swap. Everything in this function
   used to be a top-level load-time-only binding; it now runs once at load for
   the static DOM and once per injected subtree. Handler bodies are unchanged;
   only the binding selectors are scoped via root so nothing double-binds. */
function mspBindMenuInteractions(root) {
  var $root = $(root || document);

  $root.find("a.child-nav__item[hvr]").mouseenter(function () {
    var geht = $(
      ".main-nav__child.mega-nav.mega-nav--sidebar.has-motion"
    ).outerHeight();

    //console.log(geht + "geht");
    setTimeout(function () {
      var gehoutrt = $("ul.sprate_lin.child-nav").outerHeight();
      var str = gehoutrt - 23;

      $("ul.sprate_lin.child-nav,.mega_menu_right").css(
        "height",
        gehoutrt + "px"
      );
      $(".top_men_cstm.container").css("height", "712px");
      $(".main-nav__child.mega-nav.mega-nav--sidebar.has-motion").css({
        height: gehoutrt + "px",
        height: gehoutrt + "px",
      });
      $(".banner_are").css("height", gehoutrt + "px");
    }, 100);

    var hvr = $(this).attr("hvr");
    $(".all_tbs div[link]").hide();
    $('.all_tbs div[link="' + hvr + '"]').show();
    $('.all_tbs div[link="' + hvr + '"]').css("height", geht + "px");
    // $('.top_men_cstm.container,ul.sprate_lin.child-nav').css('height',geht+'px')
    var gehts = geht + 30;
    $(".banner_are").css("height", gehts + "px");

    if ($('.all_tbs div[link="' + hvr + '"]').find(".brand_img").length) {
      console.log("found image");
      var $brandImg = $('.all_tbs div[link="' + hvr + '"]').find(".brand_img");
      // v3 2026-09-03: menu images carry data-src until a human input promotes them
      var img = $brandImg.attr("src") || $brandImg.attr("data-src");
      var anc = $('.all_tbs div[link="' + hvr + '"]')
        .find(".brand_anc")
        .attr("href");
      $(".banner_are").html('<a href="' + anc + '"><img src="' + img + '"></a>');
      $("body").addClass("have_img_mnu");
      $(".banner_are").show();
    } else {
      $(".banner_are").hide();
      $("body").removeClass("have_img_mnu");
    }
  });

  /* ---- Faq code  ------ */

  $root.find(".accordion-wrapper .btn-wrapper").click(function () {
    if ($(this).hasClass("towrp")) {
    } else {
      if ($(this).parent().hasClass("active")) {
        $(".drpable").removeClass("active");
        $(".drpable .collepsing-div").slideUp();
      } else {
        $(".drpable").removeClass("active");
        $(".drpable .collepsing-div").slideUp();
        $(this).parent().addClass("active");
        $(this).next(".collepsing-div").slideDown();
      }
    }
  });

  /* ---- tab  code  ------ */

  $root.find(".tab_content").hide();
  $root.find(".tab_content:first").show();

  /* if in tab mode */
  $root.find("ul.tabs li").click(function () {
    var name = $(this).attr("name");

    $(".tab_content").hide();
    var activeTab = $(this).attr("rel");
    if (name == "brands") {
      console.log("brands clicked dd111");
      $(".accordion-wrapper.drpable").hide();
      $(
        ".accordion-wrapper.drpable.brands_menu,.accordion-wrapper.drpable.alwayshow"
      ).show();
    } else {
      $(".accordion-wrapper.drpable").show();
      $(".accordion-wrapper.drpable.brands_menu").hide();
    }
    $("#" + activeTab).fadeIn();

    $("ul.tabs li").removeClass("active");
    $(this).addClass("active");

    $(".tab_drawer_heading").removeClass("d_active");
    $(".tab_drawer_heading[rel^='" + activeTab + "']").addClass("d_active");
  });

  /* if in drawer mode */
  $root.find(".tab_drawer_heading").click(function () {
    $(".tab_content").hide();
    var d_activeTab = $(this).attr("rel");

    $("#" + d_activeTab).fadeIn();

    $(".tab_drawer_heading").removeClass("d_active");
    $(this).addClass("d_active");

    $("ul.tabs li").removeClass("active");
    $("ul.tabs li[rel^='" + d_activeTab + "']").addClass("active");
  });

  $root.find("ul.tabs li").last().addClass("tab_last");
}
mspBindMenuInteractions(document);
document.addEventListener("nav-panel:injected", function (e) {
  mspBindMenuInteractions(e.target);
});

/* Faq accordion + tab code moved into mspBindMenuInteractions above so the
   same handlers also attach to the lazy-injected mobile menu. */
$(".slider.slider-nav.thumb-image.no_slides .thumbnail-image").click(
  function () {
    var indx = $(this).index();
    console.log(indx);
    $(".slider.slider-nav.thumb-image.no_slides .thumbnail-image").removeClass(
      "active"
    );
    $(this).addClass("active");
    // $('.slider-for').slick('slickGoTo', 1);
    setTimeout(function () {
      var slider = $(".slider-for");
      slider[0].slick.slickGoTo(indx);
    }, 200);
  }
);
$(".slider-for").on("afterChange", function (event, slick, currentSlide) {
  var mediaid = $(".slider.slider-for .slick-slide.slick-current.slick-active")
    .find(".slider-banner-image")
    .attr("mediaid");
  $(".slider.slider-nav.thumb-image.no_slides .thumbnail-image").removeClass(
    "active"
  );
  $(
    '.slider.slider-nav.thumb-image.no_slides .thumbnail-image[mediaid="' +
      mediaid +
      '"]'
  ).addClass("active");
});
/* tab_drawer_heading + tab_last handling moved into mspBindMenuInteractions. */
